// Talmia II — shared pipeline / tier / data-source visual language.
//
// This file is the single source of truth for anything the UI colour-
// codes around the Pipedrive tier ladder OR the data-source classifier.
// Import from here — do NOT re-declare TIER_TONE / SOURCE_TONE etc. inside
// a component file (regression from Wave G onward).
//
// The tier list here mirrors the backend `TIER_ORDER` in
// /app/backend/pipedrive_stage_map.py. If you add a new tier there,
// add its tone entry here too and everything downstream picks it up.
import { Crown, ShieldCheck, Users2 } from "lucide-react";

// ─── Canonical tier ordering (rendering order in matrix column headers,
//     lane cards, and dropdowns). Keep in lock-step with backend.
export const TIER_ORDER = [
  "BLACK",
  "PLATINUM",
  "GOLD",
  "SILVER",
  "BRONZE",
];

// ─── Matrix / lane background+border tone (transparent, dark-theme aware).
export const TIER_TONE = {
  BLACK:      "text-slate-100 border-slate-100/40 bg-slate-100/[0.05]",
  PLATINUM:   "text-sky-200 border-sky-300/40 bg-sky-300/[0.05]",
  GOLD:       "text-amber-300 border-amber-400/40 bg-amber-400/[0.05]",
  SILVER:     "text-slate-300 border-slate-300/40 bg-slate-300/[0.05]",
  BRONZE:     "text-orange-300 border-orange-400/40 bg-orange-500/[0.05]",
  UNMAPPED:   "text-slate-500 border-slate-500/30 bg-slate-500/[0.03]",
};

// ─── Person-header / chip variant: pill styling triple + lucide icon.
//     Consumed by CRMPerson360 <TierChip> and anywhere else that shows a
//     compact tier badge next to a customer name.
export const TIER_STYLES = {
  BLACK:    { bg: "bg-slate-100/10",  text: "text-slate-100",  border: "border-slate-100/40", icon: Crown },
  PLATINUM: { bg: "bg-cyan-500/15",   text: "text-cyan-300",   border: "border-cyan-500/40",  icon: Crown },
  GOLD:     { bg: "bg-amber-500/15",  text: "text-amber-300",  border: "border-amber-500/40", icon: Crown },
  SILVER:   { bg: "bg-slate-400/15",  text: "text-slate-200",  border: "border-slate-400/40", icon: ShieldCheck },
  BRONZE:   { bg: "bg-orange-800/25", text: "text-orange-300", border: "border-orange-700/50", icon: ShieldCheck },
  DEFAULT:  { bg: "bg-white/5",       text: "text-slate-300",  border: "border-white/10",     icon: Users2 },
};

// Short-form label used on space-constrained matrix column headers.
export const SHORT_TIER = {
  UNMAPPED:   "—",
};

// ─── Data-source classifier — matches backend `DataSource` enum in
//     /app/backend/enums.py. Reused by the Data Lab source-chip filter.
export const DATA_SOURCES = ["dev", "mongo", "manual"];

export const SOURCE_TONE = {
  dev:    { dot: "bg-slate-500",   ring: "ring-slate-500/40",   label: "Dev · test data",           text: "text-slate-400" },
  mongo:  { dot: "bg-emerald-500", ring: "ring-emerald-500/40", label: "Mongo · imported",          text: "text-emerald-300" },
  manual: { dot: "bg-sky-400",     ring: "ring-sky-400/40",     label: "Manual · created in UI",    text: "text-sky-300" },
};
